import { CurrencyPipe, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, Provider, importProvidersFrom, inject } from '@angular/core';

import { MarketDisplayFallbackService, SportsMarketGroupingFallbackService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { SCROLL_ADAPTER_CONFIG, ScrollAdapterConfig } from '@frontend/sports/common/core/feature/scroll-adapter';
import { CURRENT_THEME_TOKEN, IS_ROBOT_TOKEN, SportsTheme } from '@frontend/sports/common/ui/shared';
import { provideDataRefresh } from '@frontend/sports/data-refresh/feature';
import { CustomLoadingIndicatorExtendedService, CustomLoadingIndicatorService, LOADING_INDICATOR } from '@frontend/sports/loading-indicator-feature';
import { MODAL_WINDOW_CONFIG } from '@frontend/sports/modal/feature/provider';
import { FallbackImage, PARTICIPANT_IMAGE_CONFIG } from '@frontend/sports/participant-image/feature/provider';
import { provideTracking } from '@frontend/sports/tracking/feature/network-profiler';
import { provideUser } from '@frontend/sports/user/feature';
import { DeviceService, MediaQueryService, PAGE_VIEW_DATA_PROVIDER, Page, ROUTE_PROCESSOR, STORE_PREFIX } from '@frontend/vanilla/core';
import { LANGUAGE_SWITCHER_URLS_PROVIDER } from '@frontend/vanilla/features/language-switcher';
import { RUN_AFTER_LCP_TOKEN, RunAfterLcpType } from '@frontend/vanilla/features/tracking';
import { provideNgxPopperjsOptions } from 'ngx-popperjs';
import { BetslipDigitalIntegrationModule } from 'packages/sports/common/betslip/integration/betslip-digital-integration.module';

import { AccaBoostRefreshTriggerService } from './acca-boost-base/acca-boost-refresh-trigger.service';
import { ACCA_BOOST_REFRESH_TRIGGER } from './acca-boost-base/acca-boost-token-provider.service';
import { provideBestOdds } from './best-odds-guarantee/best-odds.provider';
import { provideSportsWebBootstrap } from './bootstrap/sports-web-bootstrap.provider';
import { EpcotConfigService } from './common/epcot-config.service';
import provideEsports from './esports/esports.provider';
import { provideEventBridge } from './event-bridge/event-bridge.provider';
import { provideEventList } from './event-list-shared/event-list-providers';
import { provideMarqueeWidgetSkeleton } from './highlights-marquee/marquee-widget-skeleton.provider';
import { provideHome } from './home/home.provider';
import { provideSportsHttpInterceptors } from './http-interceptors/sports-http-interceptors.provider';
import { provideLayout } from './layout/layout.provider';
import { PatchedMediaObserver } from './layout/patched-media-observer';
import { LAZY_STYLES_CONFIG } from './lazy-features/lazy-styles.config';
import { provideMarketSubTypeLogoHideStrategy } from './marketsubtype-logo/marketsubtype-logo-hide.provider';
import { MediaProviders } from './media/media.provider';
import { provideNativeApi } from './native-api/native-api.provider';
import { provideNgrxReports } from './ngrx-reports/ngrx-reports.provider';
import { provideNumpad } from './numpad/numpad.provider';
import { provideRunAfterLcp } from './perf-cdk/run-after-lcp.provider';
import { RunAfterLcpService } from './perf-cdk/run-after-lcp.service';
import { PrettyUrlLanguageSwitcherProvider } from './router/pretty-url-language-switcher.provider';
import { PrettyUrlPageViewDataProvider } from './router/pretty-url-page-view-data.provider';
import { PrettyUrlRouteProcessor } from './router/pretty-url-route-processor.service';
import { RootInjector } from './router/root-injector.service';
import { provideRootStateStorage } from './store-persist/state-storage-root.provider';
import { provideUiManager } from './ui-manager/ui-manager.provider';

export const SCROLL_ADAPTER_CONFIG_OVERRIDE: ScrollAdapterConfig = {
    disableHorizontalScrollOnDesktop: true,
};

/**
 * Provides services needed for running the Sports-Web application as standalone or in host-app integration.
 * This should include all regular services used by the application.
 * These services will be provided as root in standalone mode and non-root in host-app integration.
 * @returns Providers needed for running Sports-Web.
 */
export function provideSportsWebCore(): (Provider | EnvironmentProviders)[] {
    return [
        importProvidersFrom(BetslipDigitalIntegrationModule.forRoot()),
        provideNgxPopperjsOptions(),
        provideEsports(),
        provideUser(),
        provideTracking(),
        provideUiManager(),
        MediaProviders(),
        provideHttpClient(withInterceptorsFromDi()),
        provideSportsHttpInterceptors(),
        provideEventList(),
        provideNgrxReports(),
        provideLayout(),
        provideNumpad(),
        provideBestOdds(),
        provideEventBridge(),
        provideDataRefresh(),
        provideNativeApi(),
        provideSportsWebBootstrap(),
        provideHome(),
        provideRootStateStorage(),
        provideMarketSubTypeLogoHideStrategy(),
        { provide: STORE_PREFIX, useValue: 'ms2.' },
        CurrencyPipe,
        RootInjector,
        provideMarqueeWidgetSkeleton(),
        { provide: MarketDisplayFallbackService, useClass: SportsMarketGroupingFallbackService },
        { provide: MediaQueryService, useClass: PatchedMediaObserver },
        {
            provide: CustomLoadingIndicatorService,
            useExisting: CustomLoadingIndicatorExtendedService,
        },
        {
            provide: LANGUAGE_SWITCHER_URLS_PROVIDER,
            useClass: PrettyUrlLanguageSwitcherProvider,
        },
        { provide: LOADING_INDICATOR, useValue: '' },
        {
            provide: PAGE_VIEW_DATA_PROVIDER,
            useClass: PrettyUrlPageViewDataProvider,
            multi: true,
        },
        {
            provide: ROUTE_PROCESSOR,
            useClass: PrettyUrlRouteProcessor,
            multi: true,
        },
        {
            provide: SCROLL_ADAPTER_CONFIG,
            useValue: SCROLL_ADAPTER_CONFIG_OVERRIDE,
        },
        {
            provide: ACCA_BOOST_REFRESH_TRIGGER,
            useClass: AccaBoostRefreshTriggerService,
        },
        {
            provide: IMAGE_LOADER,
            useValue: (config: ImageLoaderConfig) => {
                let result = config.src;
                if (result.indexOf('?') < 0) {
                    result += '?';
                } else {
                    result += '&';
                }

                if (!config.src.endsWith('.svg') && config.src.indexOf('scmedia') >= 0 && config.width) {
                    result += `w=${config.width}&`;
                }

                if (result.endsWith('?') || result.endsWith('&')) {
                    result = result.slice(0, result.length - 1);
                }

                return result;
            },
        },
        provideRunAfterLcp(),
        {
            provide: RUN_AFTER_LCP_TOKEN,
            useFactory: () => {
                const runAfterLcpService = inject(RunAfterLcpService);
                return {
                    runAfterLcp(fn: () => void) {
                        runAfterLcpService.runAfterLcp(fn);
                    },
                } satisfies RunAfterLcpType;
            },
        },
        { provide: CURRENT_THEME_TOKEN, useFactory: (page: Page) => page.theme as SportsTheme, deps: [Page] },
        { provide: IS_ROBOT_TOKEN, useFactory: (device: DeviceService) => device.isRobot, deps: [DeviceService] },
        {
            provide: MODAL_WINDOW_CONFIG,
            deps: [EpcotConfigService],
            useFactory: (epcot: EpcotConfigService) => {
                return {
                    animatePopupDismiss: epcot.isShowMyBetsPopup,
                    showCloseIcon: !epcot.isEnabled(),
                };
            },
        },
        {
            provide: LAZY_STYLES_CONFIG,
            deps: [EpcotConfig],
            useFactory: (epcot: EpcotConfig) => {
                return {
                    isEpcotEnabled: epcot.isEnabled,
                };
            },
        },
        {
            provide: PARTICIPANT_IMAGE_CONFIG,
            deps: [EpcotConfigService],
            useFactory: (epcot: EpcotConfigService) => {
                return {
                    fallbackImage: epcot.isEnabled() ? FallbackImage.EpcotDefault : FallbackImage.Default,
                };
            },
        },
    ];
}
